import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminManagementApi } from '@/lib/api';
import { dynamicFormsApi } from '@/lib/api/dynamic-forms';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePermissions } from '@/hooks/usePermissions';
import { formatAdminBusinessEt } from '@/lib/admin-datetime';
import {
  DEFAULT_SCHEDULE_TIME_ZONE,
  nextYmdInZonedCalendar,
  scheduleTimeZoneFromField,
  utcIsoStartOfZonedDay,
} from '@/lib/schedule-timezone';
import type { FormAppointmentInstructorAssignment } from '@/types/dynamic-form';
import type { FormField } from '@/types/form-definition';
import { can } from '@/utils/permissions';

type StaffRow = {
  _id: string;
  fullName: string;
  email: string;
  username?: string;
};

type Props = {
  formId: string;
  publishedVersion?: number;
  appointmentFields: { id: string; label: string }[];
  /** Draft appointment fields — schedule zones for this panel come from here (no per-field slots API probe). */
  draftAppointmentFields?: FormField[];
};

function overlapMessage(err: unknown): boolean {
  if (err && typeof err === 'object' && 'responseBody' in err) {
    const body = (err as { responseBody?: { code?: string } }).responseBody;
    return body?.code === 'INSTRUCTOR_ASSIGNMENT_OVERLAP';
  }
  return false;
}

export function FormAppointmentInstructorsPanel({
  formId,
  publishedVersion,
  appointmentFields,
  draftAppointmentFields = [],
}: Props) {
  const { permissions, adminType, loading: permLoading } = usePermissions();

  const canView = can(permissions, 'forms.appointments.instructors.view', adminType);
  const canAssign = can(permissions, 'forms.appointments.instructors.assign', adminType);
  const canViewStaff = can(permissions, 'admin.staff.view', adminType);

  const [activeFieldId, setActiveFieldId] = useState(() => appointmentFields[0]?.id ?? '');

  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);

  const [assignments, setAssignments] = useState<FormAppointmentInstructorAssignment[]>([]);
  const [assignLoading, setAssignLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [instructorId, setInstructorId] = useState('');
  const [startYmd, setStartYmd] = useState('');
  const [openEnded, setOpenEnded] = useState(true);
  const [lastInclusiveYmd, setLastInclusiveYmd] = useState('');
  const [applyToExisting, setApplyToExisting] = useState(true);
  const [staffQuery, setStaffQuery] = useState('');

  const [endOpen, setEndOpen] = useState(false);
  const [endAssignmentId, setEndAssignmentId] = useState<string | null>(null);
  const [endLastInclusiveYmd, setEndLastInclusiveYmd] = useState('');
  const [ending, setEnding] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FormAppointmentInstructorAssignment | null>(null);
  const [deleting, setDeleting] = useState(false);

  const draftFieldMap = useMemo(() => {
    const m = new Map<string, FormField>();
    for (const f of draftAppointmentFields) {
      if (f.type === 'appointment') m.set(f.id, f);
    }
    return m;
  }, [draftAppointmentFields]);

  /**
   * Resolve IANA zone per appointment field from the draft definition only.
   * Avoids N× `GET .../slots?page=1&limit=1` calls on form overview (was slow and duplicated under React StrictMode dev).
   */
  const fieldZonesResolved = useMemo(() => {
    const next: Record<string, string> = {};
    for (const f of appointmentFields) {
      const draftF = draftFieldMap.get(f.id);
      next[f.id] = draftF ? scheduleTimeZoneFromField(draftF) : DEFAULT_SCHEDULE_TIME_ZONE;
    }
    return next;
  }, [appointmentFields, draftFieldMap]);

  const zoneForActiveField = useMemo(() => {
    const z = fieldZonesResolved[activeFieldId];
    if (z) return z;
    const df = draftFieldMap.get(activeFieldId);
    return df ? scheduleTimeZoneFromField(df) : DEFAULT_SCHEDULE_TIME_ZONE;
  }, [activeFieldId, fieldZonesResolved, draftFieldMap]);

  const loadStaff = useCallback(async () => {
    if (!canViewStaff) return;
    setStaffLoading(true);
    try {
      const res = await adminManagementApi.getStaff();
      if (res.statusCode !== 200) throw new Error(res.message || 'Failed to load staff');
      const rows = (res.data.staff ?? []) as StaffRow[];
      setStaff(rows);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load staff');
      setStaff([]);
    } finally {
      setStaffLoading(false);
    }
  }, [canViewStaff]);

  const loadAssignments = useCallback(async () => {
    if (!formId || !activeFieldId) return;
    setAssignLoading(true);
    try {
      const res = await dynamicFormsApi.listInstructorAssignments(formId, activeFieldId);
      if (res.statusCode !== 200) throw new Error(res.message || 'Failed to load assignments');
      const list = [...(res.data.assignments ?? [])].sort(
        (a, b) => new Date(a.activeFrom).getTime() - new Date(b.activeFrom).getTime()
      );
      setAssignments(list);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load assignments');
      setAssignments([]);
    } finally {
      setAssignLoading(false);
    }
  }, [formId, activeFieldId]);

  useEffect(() => {
    void loadStaff();
  }, [loadStaff]);

  /** Initial / tab-change fetch; cleanup ignores stale results when deps change or in React 18 StrictMode (dev). */
  useEffect(() => {
    if (!formId || !activeFieldId) return;
    let cancelled = false;
    setAssignLoading(true);
    void (async () => {
      try {
        const res = await dynamicFormsApi.listInstructorAssignments(formId, activeFieldId);
        if (cancelled) return;
        if (res.statusCode !== 200) throw new Error(res.message || 'Failed to load assignments');
        const list = [...(res.data.assignments ?? [])].sort(
          (a, b) => new Date(a.activeFrom).getTime() - new Date(b.activeFrom).getTime()
        );
        setAssignments(list);
      } catch (e) {
        if (!cancelled) {
          toast.error(e instanceof Error ? e.message : 'Failed to load assignments');
          setAssignments([]);
        }
      } finally {
        if (!cancelled) setAssignLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formId, activeFieldId]);

  useEffect(() => {
    if (appointmentFields.length && !appointmentFields.some((a) => a.id === activeFieldId)) {
      setActiveFieldId(appointmentFields[0].id);
    }
  }, [appointmentFields, activeFieldId]);

  /** Narrows the instructor dropdown. Always keeps the current selection so the Select value does not disappear. */
  const filteredStaff = useMemo(() => {
    const q = staffQuery.trim().toLowerCase();
    const base =
      q === ''
        ? staff
        : staff.filter((s) =>
            [s.fullName, s.email, s.username].some((v) => String(v ?? '').toLowerCase().includes(q))
          );
    if (!instructorId.trim()) return base;
    const selected = staff.find((s) => s._id === instructorId);
    if (!selected || base.some((s) => s._id === instructorId)) return base;
    return [selected, ...base];
  }, [staff, staffQuery, instructorId]);

  const staffNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of staff) {
      m.set(s._id, s.fullName || s.email || s._id);
    }
    return m;
  }, [staff]);

  const resetCreate = () => {
    setInstructorId('');
    setStartYmd('');
    setOpenEnded(true);
    setLastInclusiveYmd('');
    setApplyToExisting(true);
    setStaffQuery('');
  };

  const handleCreate = async () => {
    if (!canAssign || !activeFieldId) return;
    if (!instructorId.trim()) {
      toast.error('Select an instructor.');
      return;
    }
    if (!startYmd.trim()) {
      toast.error('Start date is required.');
      return;
    }
    if (!openEnded && !lastInclusiveYmd.trim()) {
      toast.error('Last inclusive day is required when the assignment is not open-ended.');
      return;
    }
    const tz = zoneForActiveField;
    const activeFrom = utcIsoStartOfZonedDay(startYmd.trim(), tz);
    let activeTo: string | null = null;
    if (!openEnded) {
      const exclusiveYmd = nextYmdInZonedCalendar(lastInclusiveYmd.trim(), tz);
      activeTo = utcIsoStartOfZonedDay(exclusiveYmd, tz);
    }
    if (activeTo && new Date(activeTo).getTime() <= new Date(activeFrom).getTime()) {
      toast.error('End must be after start (check your last inclusive day).');
      return;
    }

    setCreating(true);
    try {
      const res = await dynamicFormsApi.createInstructorAssignment(formId, activeFieldId, {
        instructorAdminId: instructorId.trim(),
        activeFrom,
        activeTo,
        applyToExisting,
      });
      if (res.statusCode !== 201 && res.statusCode !== 200) {
        throw new Error(res.message || 'Failed to create assignment');
      }
      toast.success('Assignment created');
      setCreateOpen(false);
      resetCreate();
      await loadAssignments();
    } catch (e) {
      if (overlapMessage(e)) {
        toast.error('This range overlaps an existing assignment; adjust dates or end the previous assignment.');
      } else {
        toast.error(e instanceof Error ? e.message : 'Failed to create assignment');
      }
    } finally {
      setCreating(false);
    }
  };

  const openEndDialog = (assignmentId: string) => {
    setEndAssignmentId(assignmentId);
    setEndLastInclusiveYmd('');
    setEndOpen(true);
  };

  const openDeleteDialog = (a: FormAppointmentInstructorAssignment) => {
    setDeleteTarget(a);
    setDeleteOpen(true);
  };

  const handleDeleteAssignment = async () => {
    if (!canAssign || !activeFieldId || !deleteTarget) return;
    setDeleting(true);
    try {
      const res = await dynamicFormsApi.deleteInstructorAssignment(formId, activeFieldId, deleteTarget._id);
      if (res.statusCode !== 200) throw new Error(res.message || 'Failed to remove assignment');
      toast.success('Assignment removed');
      setDeleteOpen(false);
      setDeleteTarget(null);
      await loadAssignments();
    } catch (e) {
      const st = typeof e === 'object' && e !== null && 'status' in e ? (e as { status?: number }).status : undefined;
      if (st === 404) {
        toast.error('Assignment not found (it may have been removed already).');
      } else {
        toast.error(e instanceof Error ? e.message : 'Failed to remove assignment');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleEndAssignment = async () => {
    if (!canAssign || !activeFieldId || !endAssignmentId) return;
    if (!endLastInclusiveYmd.trim()) {
      toast.error('Last inclusive day is required.');
      return;
    }
    const tz = zoneForActiveField;
    const exclusiveYmd = nextYmdInZonedCalendar(endLastInclusiveYmd.trim(), tz);
    const activeTo = utcIsoStartOfZonedDay(exclusiveYmd, tz);
    const row = assignments.find((a) => a._id === endAssignmentId);
    if (row && new Date(activeTo).getTime() <= new Date(row.activeFrom).getTime()) {
      toast.error('End must be after this assignment’s start.');
      return;
    }

    setEnding(true);
    try {
      const res = await dynamicFormsApi.patchInstructorAssignment(formId, activeFieldId, endAssignmentId, {
        activeTo,
      });
      if (res.statusCode !== 200) throw new Error(res.message || 'Failed to update assignment');
      toast.success('Assignment ended');
      setEndOpen(false);
      setEndAssignmentId(null);
      await loadAssignments();
    } catch (e) {
      if (overlapMessage(e)) {
        toast.error('This end date overlaps another assignment; adjust the range.');
      } else {
        toast.error(e instanceof Error ? e.message : 'Failed to update assignment');
      }
    } finally {
      setEnding(false);
    }
  };

  if (permLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!canView) {
    return (
      <p className="text-sm text-muted-foreground">
        You don&apos;t have permission to view instructor assignments for this form.
      </p>
    );
  }

  if (appointmentFields.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {typeof publishedVersion !== 'number' ? (
        <p className="text-xs text-amber-800 dark:text-amber-200">
          This form has no published version yet. Date boundaries for assignments still use each field&apos;s{' '}
          <span className="font-medium">schedule time zone</span> from the draft definition (same as the form builder).
        </p>
      ) : null}

      <Tabs value={activeFieldId} onValueChange={setActiveFieldId} className="w-full">
        <TabsList
          className="flex h-auto w-full flex-wrap justify-start gap-1 bg-muted/40 p-1"
          aria-label="Appointment fields"
        >
          {appointmentFields.map((f) => (
            <TabsTrigger key={f.id} value={f.id} className="text-xs sm:text-sm">
              <span className="max-w-[200px] truncate">{f.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={activeFieldId} className="mt-4 space-y-4 outline-none">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              Schedule time zone:{' '}
              <span className="font-medium text-foreground">{zoneForActiveField}</span>
            </div>
            {canAssign ? (
              <Button
                type="button"
                size="sm"
                className="gap-2"
                onClick={() => {
                  resetCreate();
                  setCreateOpen(true);
                }}
              >
                <Plus className="h-4 w-4" />
                New assignment
              </Button>
            ) : null}
          </div>

          {assignLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : assignments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No assignments for this field yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border/60">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Instructor</TableHead>
                    <TableHead>Active from (US Eastern)</TableHead>
                    <TableHead>Active to (exclusive, US Eastern)</TableHead>
                    <TableHead className="min-w-[140px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assignments.map((a) => {
                    const name = staffNameById.get(a.instructorAdminId) ?? a.instructorAdminId;
                    const open = a.activeTo == null;
                    return (
                      <TableRow key={a._id}>
                        <TableCell className="font-medium">{name}</TableCell>
                        <TableCell className="whitespace-nowrap text-xs">{formatAdminBusinessEt(a.activeFrom)}</TableCell>
                        <TableCell className="whitespace-nowrap text-xs">
                          {a.activeTo == null ? (
                            <span className="text-muted-foreground">Open-ended</span>
                          ) : (
                            formatAdminBusinessEt(a.activeTo)
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-wrap justify-end gap-2">
                            {canAssign && open ? (
                              <Button type="button" variant="outline" size="sm" onClick={() => openEndDialog(a._id)}>
                                End assignment
                              </Button>
                            ) : null}
                            {canAssign ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="gap-1 text-destructive hover:bg-destructive/10"
                                onClick={() => openDeleteDialog(a)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Remove
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New instructor assignment</DialogTitle>
            <DialogDescription className="text-foreground">
              All dates use this appointment field&apos;s zone:{' '}
              <span className="font-medium">{zoneForActiveField}</span>.{' '}
              <span className="font-medium">Starts on</span> is the first day included;{' '}
              <span className="font-medium">Last day</span> is the last day (when not open-ended).{' '}
              <span className="font-medium">Apply to existing</span> only touches bookings whose slot time falls in that range — set{' '}
              <span className="font-medium">Starts on</span> early enough if you need older bookings updated.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            {!canViewStaff ? (
              <p className="text-sm text-destructive">
                You need <span className="font-mono text-xs">admin.staff.view</span> to load the staff list. Ask an administrator
                to grant it, or create assignments from an account that can view staff.
              </p>
            ) : staffLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <>
                <div className="grid gap-1.5">
                  <Label htmlFor="inst-search">Filter instructor list</Label>
                  <Input
                    id="inst-search"
                    value={staffQuery}
                    onChange={(e) => setStaffQuery(e.target.value)}
                    placeholder="Type to narrow by name, email, or username…"
                    className="bg-background"
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label>Instructor</Label>
                  <Select value={instructorId} onValueChange={setInstructorId}>
                    <SelectTrigger className="bg-background">
                      <SelectValue placeholder="Select SUB_ADMIN…" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredStaff.map((s) => (
                        <SelectItem key={s._id} value={s._id}>
                          {s.fullName} ({s.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="grid gap-1.5">
              <Label htmlFor="start-ymd">Starts on</Label>
              <Input
                id="start-ymd"
                type="date"
                value={startYmd}
                onChange={(e) => setStartYmd(e.target.value)}
                className="bg-background"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="open-ended"
                checked={openEnded}
                onCheckedChange={(c) => setOpenEnded(c === true)}
              />
              <Label htmlFor="open-ended" className="cursor-pointer text-sm font-normal leading-none">
                Open-ended (no fixed end; use &quot;End assignment&quot; later)
              </Label>
            </div>

            {!openEnded ? (
              <div className="grid gap-1.5">
                <Label htmlFor="last-inc">Last day (inclusive)</Label>
                <Input
                  id="last-inc"
                  type="date"
                  value={lastInclusiveYmd}
                  onChange={(e) => setLastInclusiveYmd(e.target.value)}
                  className="bg-background"
                />
              </div>
            ) : null}

            <div className="flex items-center gap-2">
              <Checkbox
                id="apply-existing"
                checked={applyToExisting}
                onCheckedChange={(c) => setApplyToExisting(c === true)}
              />
              <Label htmlFor="apply-existing" className="cursor-pointer text-sm font-normal leading-none">
                Apply to existing slots and bookings in this range (recommended)
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Bookings outside this date range are left as they are. Check slot dates on the attendance list if something is missing.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleCreate()} disabled={creating || !canViewStaff}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          setDeleteOpen(open);
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove instructor assignment?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              This deletes the assignment record only. It does <span className="font-medium text-foreground">not</span> change{' '}
              <span className="font-mono text-xs">instructorAdminId</span> on existing bookings or slots (those stay as they were
              after booking or backfill). Overlap checks for new assignments will no longer see this row.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              onClick={(ev) => {
                ev.preventDefault();
                void handleDeleteAssignment();
              }}
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Remove assignment'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={endOpen} onOpenChange={setEndOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>End assignment</DialogTitle>
            <DialogDescription className="text-foreground">
              The assignment stops after the last day you pick (in {zoneForActiveField}).
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5 py-2">
            <Label htmlFor="end-last">Last inclusive day</Label>
            <Input
              id="end-last"
              type="date"
              value={endLastInclusiveYmd}
              onChange={(e) => setEndLastInclusiveYmd(e.target.value)}
              className="bg-background"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEndOpen(false)} disabled={ending}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleEndAssignment()} disabled={ending}>
              {ending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
